import swal from 'sweetalert2';

/**
 * VEC-3316 — el flujo de solicitud de stock, compartido entre pantallas.
 *
 * Vive acá y no dentro de un componente porque lo usan DOS pantallas con reglas distintas:
 *
 *   - la APERTURA del correctivo (`CorrectivosAbm`): la solicitud es OPCIONAL, así que primero
 *     pregunta y solo si el usuario dice que sí abre el formulario de cantidades;
 *   - la carga del PRESUPUESTO DETALLADO (`TicketPresupuestoDetallado`): es BLOQUEANTE — sin
 *     stock no se puede guardar—, así que va directo al formulario, sin pregunta previa.
 *
 * Son funciones y no componentes React a propósito: SweetAlert2 vive fuera del árbol de React y
 * las dos pantallas necesitan `await`ear una decisión en medio de un handler, no renderizar algo.
 *
 * ⚠️ EL CONTENIDO SE ARMA CON DOM Y NO CON UN STRING HTML. `swal html:` con interpolación es un
 * XSS sistémico en este repo —en este módulo ya se corrigió una vez, en VEC-3312— y acá se
 * interpolarían descripciones de ítems, que las carga un usuario. Con `createElement` y
 * `textContent` no hay forma de que una descripción se ejecute.
 *
 * ⚠️ `type:` y no `icon:`: sweetalert2 está en la 7.33 y `icon` recién existe desde la v9.
 */

/** Lo que falta de un ítem para cubrir lo pedido. Nunca negativo. */
export const faltanteDe = (item) => {
	const disponible = item ? item.stockDisponible : null;
	const necesaria = Number((item && item.cantidad) || 0);

	if (disponible === null || disponible === undefined) return necesaria;

	return Math.max(necesaria - disponible, 0);
};

/**
 * ¿Este ítem pide más de lo que hay?
 *
 * `stockDisponible` en null NO cuenta como faltante: es "no se puede saber" (alta masiva,
 * correctivo de llanta, móvil sin base) y ahí no hay contra qué comparar. Solo el 0 y los números
 * concretos disparan el aviso.
 */
export const tieneFaltante = (item) =>
	item &&
	item.stockDisponible !== null &&
	item.stockDisponible !== undefined &&
	Number(item.cantidad || 0) > item.stockDisponible;

/** Resultados posibles de la pregunta previa. */
export const SOLICITAR = 'solicitar';
export const SIN_SOLICITUD = 'sin_solicitud';
export const CANCELAR = 'cancelar';

/**
 * Paso PREVIO, solo para las pantallas donde la solicitud es opcional.
 *
 * ⚠️ TRES SALIDAS CON DOS BOTONES. `showDenyButton` recién existe desde la v10, así que el tercer
 * camino sale del `dismiss`:
 *   - confirmar -> SOLICITAR      (abrir el formulario de cantidades)
 *   - cancelar  -> SIN_SOLICITUD  (seguir sin pedir nada)
 *   - X / Esc   -> CANCELAR       (volver al formulario, sin hacer nada)
 * El clic afuera queda deshabilitado a propósito: con tres caminos, un clic al voleo no puede
 * decidir por el usuario.
 *
 * Lista los faltantes como TEXTO y no como inputs —las cantidades se ajustan en el paso
 * siguiente—, para que la decisión no sea a ciegas.
 *
 * @returns {Promise<'solicitar'|'sin_solicitud'|'cancelar'>}
 */
export const preguntarSiSolicitaStock = (intl, faltantes) => {
	const contenedor = document.createElement('div');

	const leyenda = document.createElement('p');
	leyenda.textContent = intl.formatMessage({
		id: 'correctivosAbm.solicitudStock.pregunta_leyenda',
		defaultMessage: 'No hay stock suficiente para estos repuestos. ¿Querés generar una solicitud de stock?',
	});
	contenedor.appendChild(leyenda);

	const lista = document.createElement('ul');
	lista.style.textAlign = 'left';
	lista.style.display = 'inline-block';

	(faltantes || []).forEach((item) => {
		const fila = document.createElement('li');

		fila.textContent = item.descripcion + ' — ' + intl.formatMessage(
			{
				id: 'correctivosAbm.solicitudStock.pregunta_faltante',
				defaultMessage: 'faltan {falta} de {necesaria} (hay {disponible})',
			},
			{ falta: faltanteDe(item), necesaria: Number(item.cantidad || 0), disponible: item.stockDisponible }
		);

		lista.appendChild(fila);
	});

	contenedor.appendChild(lista);

	return swal({
		title: intl.formatMessage({
			id: 'correctivosAbm.solicitudStock.pregunta_titulo',
			defaultMessage: '¿Generar una solicitud de stock?',
		}),
		html: contenedor,
		type: 'question',
		customClass: 'swal-wide',
		allowOutsideClick: false,
		showCloseButton: true,
		showCancelButton: true,
		confirmButtonText: `<i style="margin-right:5px" class="fa fa-check-circle"></i>${intl.formatMessage({ id: 'correctivosAbm.solicitudStock.pregunta_si', defaultMessage: ' Sí, generar solicitud' })}`,
		cancelButtonText: `<i style="margin-right:5px" class="fa fa-arrow-circle-right"></i>${intl.formatMessage({ id: 'correctivosAbm.solicitudStock.pregunta_no', defaultMessage: ' No, crear sin solicitud' })}`,
		confirmButtonColor: '#0ABFBC',
		// Gris y no rojo: el cancelar de este modal NO es la salida segura sino un camino
		// alternativo hacia adelante. Mismo criterio que MovilesGridRow:327, el otro modal del
		// repo con la semántica invertida. Volver atrás es la X / Esc.
		cancelButtonColor: '#bfbcbc',
		focusConfirm: false,
	}).then((res) => {
		if (res.value) return SOLICITAR;

		return res.dismiss === swal.DismissReason.cancel ? SIN_SOLICITUD : CANCELAR;
	});
};

/**
 * ⚠️ PARA QUIEN IMPLEMENTE VEC-3317 — FALTA EL AVISO DE "SOLICITUD ENVIADA"
 *
 * Hoy este modal NO envía nada: resuelve con las cantidades cargadas y el caller decide qué hacer
 * con ellas (el alta del correctivo las guarda en memoria, el presupuesto directamente las
 * descarta). Por eso no hay confirmación de envío: no habría nada que confirmar.
 *
 * Cuando 3317 persista la solicitud, el confirmar tiene que **esperar la respuesta del endpoint y
 * mostrarle al usuario si se envió bien o falló** — pedido explícito de Ivan (11/09). Cerrar el
 * modal a ciegas, como hace ahora, dejaría al usuario sin saber si pidió el repuesto o no.
 *
 * Cómo, sin romper el flujo: `swal` cierra apenas se aprieta confirmar, así que el POST va en
 * `preConfirm` con `showLoaderOnConfirm: true` —que mantiene el modal abierto con el spinner— y el
 * resultado se muestra ahí mismo; si falla, `swal.showValidationMessage()` deja al usuario en el
 * formulario con las cantidades cargadas en vez de perdérselas.
 *
 * Ojo con los dos llamadores, que tienen contratos distintos: en el presupuesto el botón ya se llama
 * "Enviar solicitud de stock" y el guardado queda bloqueado igual (el gate del backend), mientras
 * que en el alta del correctivo dice "Crear el ticket" y sigue adelante con la creación.
 */
/**
 * El formulario de cantidades.
 *
 * LISTA TODOS LOS REPUESTOS, no solo los que faltan: la solicitud es "lo que necesito para este
 * trabajo", así que el usuario ajusta el pedido completo en un solo lugar. Los que están en falta
 * van marcados para que se distingan de un vistazo, y cada cantidad viene PRECARGADA con el
 * faltante — lo que se necesita menos lo disponible—, que es lo que hay que pedir. Los que ya
 * tienen stock suficiente arrancan en 0.
 *
 * @param {object} intl
 * @param {Array} items repuestos ya agregados (sin mano de obra)
 * @param {string} textoConfirmar etiqueta del botón que sigue adelante, propia de cada pantalla
 * @returns {Promise<Array<{item, descripcion, cantidad}>|null>} null si el usuario canceló
 */
export const pedirCantidadesSolicitud = (intl, items, textoConfirmar) => {
	const contenedor = document.createElement('div');

	const leyenda = document.createElement('p');
	leyenda.textContent = intl.formatMessage({
		id: 'correctivosAbm.solicitudStock.leyenda',
		defaultMessage: 'No hay stock suficiente para todos los repuestos. Revisá las cantidades a solicitar antes de continuar.',
	});
	contenedor.appendChild(leyenda);

	const tabla = document.createElement('table');
	tabla.className = 'table table-sm';

	const thead = document.createElement('thead');
	const filaTitulos = document.createElement('tr');
	[
		intl.formatMessage({ id: 'Repuestos', defaultMessage: 'Repuestos' }),
		intl.formatMessage({ id: 'correctivosAbm.repuestos.columna_stock', defaultMessage: 'Stock' }),
		intl.formatMessage({ id: 'correctivosAbm.solicitudStock.columna_solicitar', defaultMessage: 'A solicitar' }),
	].forEach((titulo) => {
		const th = document.createElement('th');
		th.textContent = titulo;
		filaTitulos.appendChild(th);
	});
	thead.appendChild(filaTitulos);
	tabla.appendChild(thead);

	const tbody = document.createElement('tbody');
	const inputs = [];

	(items || []).forEach((item) => {
		const fila = document.createElement('tr');

		const celdaItem = document.createElement('td');
		celdaItem.textContent = item.descripcion;
		celdaItem.style.textAlign = 'left';
		fila.appendChild(celdaItem);

		const disponible = item.stockDisponible;

		const celdaStock = document.createElement('td');
		celdaStock.textContent =
			disponible === null || disponible === undefined ? '-' : String(disponible);
		if (tieneFaltante(item)) celdaStock.style.color = '#FF4558';
		fila.appendChild(celdaStock);

		const celdaCantidad = document.createElement('td');
		const input = document.createElement('input');
		input.type = 'number';
		input.min = '0';
		// Un envase es indivisible: `comprometerPorPresupuesto()` reserva con `ceil()`, así que pedir
		// 1,5 no significa nada. El `step` lo señala en el control y el `preConfirm` lo rechaza.
		input.step = '1';
		input.className = 'form-control form-control-sm';
		input.value = String(faltanteDe(item));
		input.dataset.itemId = String(item.id);
		celdaCantidad.appendChild(input);
		fila.appendChild(celdaCantidad);

		inputs.push({ item: item, input: input });
		tbody.appendChild(fila);
	});

	tabla.appendChild(tbody);
	contenedor.appendChild(tabla);

	return swal({
		title: intl.formatMessage({
			id: 'correctivosAbm.solicitudStock.titulo',
			defaultMessage: 'Hay que crear una solicitud de stock',
		}),
		html: contenedor,
		type: 'warning',
		customClass: 'swal-wide',
		allowOutsideClick: false,
		showCancelButton: true,
		cancelButtonText: `<i style="margin-right:5px" class="fa fa-times-circle"></i>${intl.formatMessage({ id: 'cancelar', defaultMessage: ' Cancelar' })}`,
		confirmButtonText: `<i style="margin-right:5px" class="fa fa-check-circle"></i>${textoConfirmar}`,
		cancelButtonColor: '#FF3145',
		confirmButtonColor: '#0ABFBC',
		focusConfirm: false,
		/**
		 * VEC-3319 Sc. 04 — no se confirma una solicitud sin una cantidad válida.
		 *
		 * ⚠️ NO SE VALIDA "CADA LÍNEA > 0", Y NO ES UN OLVIDO. El formulario lista TODOS los
		 * repuestos y precarga el faltante, así que los que ya tienen stock arrancan en 0: ahí el
		 * 0 significa "de este no pido nada" y es la respuesta correcta. Lo que el escenario
		 * quiere evitar es confirmar sin pedir nada, que se lee sobre el conjunto y no sobre el
		 * campo. Por lo mismo el input conserva `min='0'`.
		 *
		 * `showValidationMessage()` + `return false` es la forma de la v7.33: sin
		 * `expectRejections`, devolver `false` es lo que le dice a SweetAlert que no cierre
		 * (`sweetalert2.js:2118`). El modal queda abierto con las cantidades cargadas, que es el
		 * punto — perderlas obligaría a recargar todo el pedido. `showValidationError()` está
		 * deprecado en esta misma versión, así que no se usa.
		 *
		 * Acá es donde la card que persiste la solicitud va a colgar su POST, con
		 * `showLoaderOnConfirm: true`: esta validación corre antes y le deja el gancho puesto.
		 */
		preConfirm: () => {
			// El campo vacío se mira ANTES de convertir: `Number('')` es 0, que es un valor válido
			// —"de este no pido nada"— y sin este chequeo un campo borrado a mano se confundía con
			// un cero deliberado y salía el mensaje equivocado.
			const hayVacios = inputs.some((fila) => String(fila.input.value).trim() === '');
			const cantidades = inputs.map((fila) => Number(fila.input.value));
			const hayInvalidas = cantidades.some(
				(cantidad) => !Number.isInteger(cantidad) || cantidad < 0
			);

			if (hayVacios || hayInvalidas) {
				swal.showValidationMessage(
					intl.formatMessage({
						id: 'solicitudStock.error.cantidad_invalida',
						defaultMessage: 'Las cantidades tienen que ser números enteros mayores o iguales a cero.',
					})
				);

				return false;
			}

			if (!cantidades.some((cantidad) => cantidad > 0)) {
				swal.showValidationMessage(
					intl.formatMessage({
						id: 'solicitudStock.error.sin_cantidades',
						defaultMessage: 'Hay que pedir al menos un repuesto para poder continuar.',
					})
				);

				return false;
			}

			return true;
		},
	}).then((res) => {
		// Cancelar vuelve al formulario con todo cargado y sin seguir adelante: es la salida para
		// corregir las cantidades.
		if (!res.value) return null;

		return inputs.map((fila) => ({
			item: fila.item.id,
			descripcion: fila.item.descripcion,
			cantidad: fila.input.value === '' ? 0 : Number(fila.input.value),
		}));
	});
};
